import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { FormValidationService } from '../../../../shared/services/form-validation.service';
import { ReferenceAreaEditFormService } from '../../services/reference-area-edit-form.service';
import { ReferenceArea } from '../../models/reference-area';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { ReferenceAreaDataService } from '../../services/reference-area-data.service';
import { MessageService } from 'primeng/api';
@Component({
  selector: 'app-reference-area-import',
  template: require('./reference-area-import.component.html'),
  styles: [require('./reference-area-import.component.sass')]
})
export class ReferenceAreaImportComponent implements OnInit {

  @ViewChild('referenceAreaImportModal') referenceAreaImportModal: ElementRef;
  @Input() referenceAreas: ReferenceArea[];
  @Output() importReferenceAreas = new EventEmitter<FormData>();
  @Output() reload = new EventEmitter();

  projectId: number;
  referenceAreaImportForm: FormGroup;
  referenceAreaImportModalReference: NgbModalRef;
  fileToUpload: File;
  displayHelp: boolean = false;
  fileName: string;
  importErrors: (ReferenceArea & {
    rowId: number;
    errorMsgs: String[]
  })[];
  fileIsEmpty = false;
  fileIsInvalid = false;

  constructor(
    private fb: FormBuilder,
    private referenceAreaEditFormService: ReferenceAreaEditFormService,
    private modalService: NgbModal,
    public formValidation: FormValidationService,
    private translateService: TranslateService,
    private referenceDataService: ReferenceAreaDataService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    this.referenceAreaImportForm = this.fb.group({
      fileInput: ['', [Validators.required, validateFileFormat]]
    });
  }

  ngOnInit(): void {
    this.referenceAreaImportForm.valueChanges.subscribe(values => {
    })
  }

  async showModal(projectId: number) {
    this.fileName = '';
    this.importErrors = [];
    this.referenceAreaImportForm.reset();
    this.projectId = projectId;

    this.referenceAreaImportModalReference = await this.modalService.open(this.referenceAreaImportModal, {
      size: 'xl'
    })
  }

  handleFileInput(event) {
    this.fileToUpload = event.target.files[0];
    if (this.fileToUpload) {
      this.fileName = this.fileToUpload.name;
    }
  }

  save = () => {
    this.importErrors = [];
    let existingReferences = [...this.referenceAreas];
    this.fileIsEmpty = false;
    this.fileIsInvalid = false;

    if (this.referenceAreaImportForm.valid) {
      if (this.fileToUpload) {
        const reader = new FileReader();
        reader.onload = async (e: any) => {
          const content: string = e.target.result;
          const lines = content.split(/\r?\n/);
          const headers = lines[0].split(';');
          let importedReferenceAreas: any[] = [];
          try {
            for (let rowId = 1; rowId < lines.length; rowId++) {
              if (lines[rowId]) {
                const values = lines[rowId].split(';');
                let referenceArea: any = {};
                referenceArea.projectId = this.projectId;
                for (let idx = 0; idx < headers.length; idx++) {
                  referenceArea[headers[idx].replace(/['"]+/g, '')] = values[idx].replace(/^"|"$/g, '')
                }
  
                referenceArea.type = referenceArea.type === "STATION" ? "WAYPOINT": referenceArea.type;
  
                try {
                  const referenceAreaForm = this.referenceAreaEditFormService.generate(existingReferences, [...environment.config.referenceAreas.types]);
                  referenceAreaForm.patchValue({
                    ...referenceArea,
                    latLongList: referenceArea.area
                  })
                  if (referenceAreaForm.valid) {
                    referenceArea = {
                      ...referenceArea,
                      area: {
                        positions: referenceArea.area?.split(" ").map(position => {
                          const latLong = position.split(",");
                          return {
                            latitude: latLong[0],
                            longitude: latLong[1]
                          }
                        })
                      }
                    }
  
                    importedReferenceAreas = [
                      ...importedReferenceAreas,
                      referenceArea
                    ]
                  } else {
                    let errorMsgs: String[] = [];
                    errorMsgs = Object.keys(referenceAreaForm.controls).reduce((acc, key) => {
                      if (!referenceAreaForm.controls[key].valid) {
                        const keyErrors = referenceAreaForm.controls[key].errors
                        if (keyErrors) {
                          key = key === 'latLongList' ? 'area': key;
                          acc[key] = Object.keys(keyErrors).reduce((errorArray: String[], keyError) => {
                            let translatableErrorMsg;
                            let errorDetails = {};
                            switch (keyError) {
                              case 'required':
                                translatableErrorMsg = 'I18N_GENERAL.FIELD_REQUIRED';
                                break;
                              case 'duplicate':
                                translatableErrorMsg = 'I18N_GENERAL.FIELD_DUPLICATE';
                                break;
                              case 'minLengthLimit':
                                translatableErrorMsg = 'I18N_GENERAL.FIELD_MIN_LENGTH';
                                errorDetails = {
                                  error: keyErrors.minLengthLimit
                                }
                                break;
                              case 'maxLengthLimit':
                                translatableErrorMsg = 'I18N_GENERAL.FIELD_MAX_LENGTH';
                                errorDetails = {
                                  error: keyErrors.maxLengthLimit
                                }
                                break;
                              case 'minPositions':
                                translatableErrorMsg = 'I18N_REFERENCE_AREAS.AREA_MIN_POSITIONS';
                                errorDetails = {
                                  minError: keyErrors.minPositions, 
                                  actualError: keyErrors.actualPositions
                                }
                                break;
                              case 'cannotParsePosition':
                                translatableErrorMsg = 'I18N_REFERENCE_AREAS.AREA_PARSE_POSITIONS';
                                errorDetails = {
                                  error: keyErrors.cannotParsePosition
                                }
                                break;
                              case 'latitudeMissing':
                                translatableErrorMsg = 'I18N_REFERENCE_AREAS.AREA_MISSING_LAT';
                                errorDetails = {
                                  error: keyErrors.latitudeMissing
                                }
                                break;
                              case 'longitudeMissing':
                                translatableErrorMsg = 'I18N_GENERAL.AREA_MISSING_LONG';
                                errorDetails = {
                                  error: keyErrors.longitudeMissing
                                }
                                break;
                              case 'latitudeInvalid':
                                translatableErrorMsg = 'I18N_REFERENCE_AREAS.AREA_PARSE_LAT';
                                errorDetails = {
                                  error: keyErrors.latitudeInvalid
                                }
                                break;
                              case 'longitudeInvalid':
                                translatableErrorMsg = 'I18N_REFERENCE_AREAS.AREA_PARSE_LONG';
                                errorDetails = {
                                  error: keyErrors.longitudeInvalid
                                }
                                break;
                              case 'invalidValue':
                                  translatableErrorMsg = 'I18N_GENERAL.FIELD_INVALID';
                                  if (key === 'type') {
                                    translatableErrorMsg = 'I18N_REFERENCE_AREAS.INVALID_TYPE';
                                  }
                                break;
                              default:
                                translatableErrorMsg = 'I18N_GENERAL.FIELD_INVALID';
                                break;
                            }
                            errorArray = [
                              ...errorArray, 
                              this.translateService.instant(`${translatableErrorMsg}`, errorDetails)
                            ]
                            return errorArray;
                          }, [])
                        }
                      }
                      return acc;
                    }, errorMsgs);
                    this.importErrors = [
                      ...this.importErrors,
                      {
                        ...referenceArea,
                        rowId,
                        errorMsgs
                      }
                    ]
                  }
                  existingReferences = [
                    ...existingReferences,
                    referenceArea
                  ]
  
                } catch (error) {
                  this.importErrors = [
                    ...this.importErrors,
                    {
                      ...referenceArea,
                      rowId,
                      errorMsgs: [JSON.stringify(error)]
                    }
                  ]
                }
              }
            }
            if (this.importErrors.length === 0) {
              this.fileIsEmpty = importedReferenceAreas.length === 0;
              if (!this.fileIsEmpty) {
                try {
                  await this.referenceDataService.saveReferenceAreas(this.projectId, importedReferenceAreas);
                  this.messageService.add({
                    severity:'success',
                    summary: this.translate.instant('I18N_REFERENCE_AREAS.IMPORT_REFERENCE_AREAS'),
                    detail:`${this.translate.instant("I18N_REFERENCE_AREAS.IMPORT_SUCCESS")}`
                  });
                  this.referenceAreaImportModalReference.close();
                  this.reload.emit();
                } catch(error) {
                  this.messageService.add({
                    severity:'error',
                    summary: this.translate.instant("I18N_REFERENCE_AREAS.IMPORT_REFERENCE_AREAS"),
                    detail: error && error.message
                  });
                }  
              }
            }
          } catch (error) {
            this.fileIsInvalid = true;
          }
   

        };
        reader.readAsText(this.fileToUpload);
      }
    } else {
      this.formValidation.validateAllFormFields(this.referenceAreaImportForm);
    }
  }

}

export function validateFileFormat(control: AbstractControl) {
  if (control.value && !control.value.endsWith('csv')) {
    return { wrongFileType: true };
  }
  return null;
}
